import React, {useContext, useEffect, useState, useRef} from "react";
import '../../App.css';
import DataGrid, {
    HeaderFilter, SearchPanel, Editing,
    Popup, LoadPanel, Lookup, Toolbar, Item as TItem,
    Form, Column, ColumnChooser, Sorting, SimpleItem
} from 'devextreme-react/data-grid';
import SelectBox from "devextreme-react/select-box";
import {
    Item, RequiredRule, AsyncRule, Label, GroupItem
} from 'devextreme-react/form';
import AuthContext from "../../contexts/AuthContext";
import "devextreme-react/select-box";
import {Scrolling} from "devextreme-react/tree-list";
import deMessages from "devextreme/localization/messages/de.json";
import { locale, loadMessages } from "devextreme/localization";
import { DataStore, Hub } from "aws-amplify";
import { Customer, UserRole, Gender, CountryCode} from "../../models";
import {useTranslation} from "react-i18next";
import {customersStore, handleSubscriptionEvent} from '../../data/customersStore';
import PageHeader from "../PageHeader/PageHeader";

function CustomerManagementView() {
    loadMessages(deMessages);
    locale(navigator.language);
    const [t] = useTranslation();
    const gender = t('gender',{ returnObjects: true });
    const countries = t('countries',{ returnObjects: true });
    const userContext = useContext(AuthContext);
    const [editing, setEditing] = useState(false);
    const [filter, setFilter] = useState([]);
    const datagrid = useRef(null);

    const getFilteredGender = (options) => {
        let filter = null;
        return {
            store: gender,
            filter:filter
        };
    }

    const getFilteredCountries = (options) => {
        let filter = null;
        return {
            store: countries,
            filter:filter
        };
    }

    const renderAddressCell = (data) => {
        if (data.value) {
            return (
                <div className={"customer address"}>
                    <span>{data.value.street} {data.value.streetNr}</span>
                    <span>{data.value.zipCode} {data.value.city}</span>
                    <span>{data.value.country}</span>
                </div>
            )}
    }

    const allowDeleting = (e) => {
        // SuperAdmin can edit anything
        if (UserRole.SUPER_ADMIN === userContext.userInfoFromDB.role) {
            return true;
        }
        return [UserRole.ADMIN].includes(userContext.userInfoFromDB.role);
    }

    const allowEditing = (e) => {
        // SuperAdmin can edit anything except other SUPER_ADMIN
        if (UserRole.SUPER_ADMIN === userContext.userInfoFromDB.role) {
            return true;
        }
        return [UserRole.ADMIN].includes(userContext.userInfoFromDB.role);
    }

    const addCustomer = async (event) => {
        if (![UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(userContext.userInfoFromDB.role)) {
            alert(`Benutzer mit Rolle ${userContext.userInfoFromDB.role} dürfen keine Bürger anlegen`);
            event.cancel = true;
            return;
        }
    }

    const editCustomer = async (event) => {
        if (![UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(userContext.userInfoFromDB.role)) {
            alert(`Benutzer mit Rolle ${userContext.userInfoFromDB.role} dürfen keine Bürger bearbeiten`);
            event.cancel = true;
            return;
        }
    }

    const removeCustomer = async (event) => {
        if (![UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(userContext.userInfoFromDB.role)) {
            alert(`Benutzer mit Rolle ${userContext.userInfoFromDB.role} dürfen keine Bürger löschen`);
            event.cancel = true;
            return;
        }
    }

    const onInitNewRow = (event) => {
        // set here default values
        setEditing(false);
        event.data.address = {
            country: CountryCode.A
        };
        //event.data.status='ACTIVE';
    }

    useEffect(() => {
        const hubListenerDS = async (hubData) => {
            const  { event } = hubData.payload;
            if (event === 'ready') {
                datagrid?.current?.instance?.refresh(true);
            }
        };
        Hub.listen('datastore', hubListenerDS);
        const subscription = DataStore.observe(Customer).subscribe(handleSubscriptionEvent);
        return () => {
            subscription.unsubscribe();
            Hub.remove('datastore', hubListenerDS);
        }
    }, []);

    if (userContext.userInfoFromDB && [UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(userContext.userInfoFromDB?.role)) {
        return (
            <div className="CustomerManagementView">
                <PageHeader headerText={t('customerManagementView.header')} />
                <DataGrid
                    ref={ref => datagrid.current = ref}
                    dataSource={customersStore}
                    defaultFilterValue={[]}
                    columnAutoWidth={true}
                    columnHidingEnabled={true}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    showColumnLines={false}
                    showRowLines={false}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    remoteOperations={{
                        paging: true,
                        sorting: false,
                        filtering: false
                    }}
                    id="customers"
                    onRowInserting={e => addCustomer(e)}
                    onRowUpdating={e => editCustomer(e)}
                    onInitNewRow={e => onInitNewRow(e)}
                    onEditingStart={() => {setEditing(true)}}
                    onRowRemoving={e => removeCustomer(e)}
                >
                    <ColumnChooser enabled={false} mode={"select"}/>
                    <LoadPanel enabled/>
                    <Sorting mode="single" />
                    <Scrolling mode="infinite" />
                    <SearchPanel visible={true} width={"auto"}/>
                    <HeaderFilter visible={true}/>
                    <Toolbar>
                        <TItem name="addRowButton" location={"after"} locateInMenu={"auto"}/>
                        <TItem
                            name="columnChooserButton"
                            locateInMenu="auto"
                            location="after"
                        />
                        <TItem name="searchPanel" location={"after"} locateInMenu={"auto"}/>
                    </Toolbar>
                        <Editing
                            mode={"popup"}
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={allowEditing}
                            allowDeleting={allowDeleting}
                            >
                            <Popup title={!editing ? t('customerManagementView.popupTitle1') : t('customerManagementView.popupTitle2')}
                                   showTitle={true}
                                   width="auto"
                                   //minWidth={"50vw"}
                                   maxHeight={"100vh"}
                                   height="auto"/>
                            <Form labelMode={'outside'} colCount={2} showValidationSummary={true}>
                                <GroupItem caption={t('customerManagementView.groupItem1')}>
                                    <Item dataField="customerId" colSpan={1}>
                                        <RequiredRule message={t('customerManagementView.customerIdRequiredRuleMsg')}/>
                                    </Item>
                                    <Item dataField="firstName" colSpan={1}>
                                        <RequiredRule message={t('customerManagementView.firstNameRequiredRuleMsg')}/>
                                    </Item>
                                    <Item dataField="lastName" colSpan={1}>
                                        <RequiredRule message={t('customerManagementView.lastNameRequiredRuleMsg')}/>
                                    </Item>
                                    <Item dataField={"gender"} colSpan={1}/>
                                    <Item dataField="phone" colSpan={1}/>
                                    <Item dataField="email" colSpan={1}/>
                                </GroupItem>
                                <GroupItem caption={t('customerManagementView.groupItem2')}>
                                    <Item dataField={"address.street"} colSpan={1}/>
                                    <Item dataField={"address.streetNr"} colSpan={1}/>
                                    <Item dataField={"address.zipCode"} colSpan={1}/>
                                    <Item dataField={"address.city"} colSpan={1}/>
                                    <Item dataField={"address.country"} colSpan={1}/>
                                </GroupItem>
                            </Form>
                        </Editing> : null}
                    <Column dataField="customerId"
                            caption={t('customerManagementView.customerId')}
                            defaultSortOrder={"asc"}
                            allowEditing={!editing}
                            hidingPriority={11}/>
                    <Column dataField="lastName"
                            caption={t('customerManagementView.lastName')}
                            hidingPriority={10}
                    />
                    <Column dataField="firstName"
                            caption={t('customerManagementView.firstName')}
                            hidingPriority={7}/>
                    <Column dataField="gender" caption={t('customerManagementView.gender')} hidingPriority={5}>
                        <Lookup dataSource={getFilteredGender} valueExpr="id" displayExpr={"name"} searchEnabled={true}/>
                    </Column>
                    <Column dataField="phone" caption={t('customerManagementView.phone')} hidingPriority={8}/>
                    <Column dataField="email" caption={t('customerManagementView.email')} hidingPriority={9}/>
                    <Column dataField="address"
                            caption={t('customerManagementView.address')}
                            cellRender={renderAddressCell}
                            hidingPriority={6}
                            allowSorting={false}
                            allowFiltering={false}/>
                    <Column dataField="address.street"
                            caption={t('customerManagementView.street')}
                            visible={false}/>
                    <Column dataField="address.streetNr"
                            caption={t('customerManagementView.streetNr')}
                            visible={false}/>
                    <Column dataField="address.zipCode"
                            caption={t('customerManagementView.zipCode')}
                            visible={false}/>
                    <Column dataField="address.city"
                            caption={t('customerManagementView.city')}
                            visible={false}/>
                    <Column dataField="address.country"
                            caption={t('customerManagementView.country')}
                            visible={false}>
                        <Lookup dataSource={getFilteredCountries} valueExpr="id" displayExpr={"name"} searchEnabled={true}/>
                    </Column>
                </DataGrid>
            </div>
        );
    } else {
        return null;
    }
}

export default CustomerManagementView;
